import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Alert,
  Typography,
  Card,
  Space,
  Button,
  List,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminWithdrawalEndpoints, type AdminWithdrawalDetail } from '../api/adminWithdrawalEndpoints';

const { Text } = Typography;

interface WithdrawalRequestDetailDrawerProps {
  withdrawalId: string | null;
  open: boolean;
  onClose: () => void;
  onTransferClick?: (transferId: string) => void;
  zIndex?: number;
}

const WithdrawalRequestDetailDrawer: React.FC<WithdrawalRequestDetailDrawerProps> = ({
  withdrawalId,
  open,
  onClose,
  onTransferClick,
  zIndex = 1001,
}) => {
  const [loading, setLoading] = useState(false);
  const [withdrawal, setWithdrawal] = useState<AdminWithdrawalDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && withdrawalId) {
      fetchWithdrawalDetail();
    }
  }, [open, withdrawalId]);

  const fetchWithdrawalDetail = async () => {
    if (!withdrawalId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminWithdrawalEndpoints.getDetail(withdrawalId);
      setWithdrawal(data);
    } catch (err: any) {
      console.error('Failed to fetch withdrawal details:', err);
      setError(err.response?.data?.error || 'Failed to load withdrawal details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY h:mm:ss A');
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode }> = {
      pending: { color: 'orange', icon: <ClockCircleOutlined /> },
      approved: { color: 'blue', icon: <CheckCircleOutlined /> },
      rejected: { color: 'red', icon: <CloseCircleOutlined /> },
      done: { color: 'green', icon: <CheckCircleOutlined /> },
      canceled: { color: 'default', icon: <CloseCircleOutlined /> },
      partially_paid: { color: 'cyan', icon: <ClockCircleOutlined /> },
    };
    return configs[status] || { color: 'default', icon: null };
  };

  const getTransferStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'orange',
      processing: 'blue',
      succeeded: 'green',
      failed: 'red',
      cancelled: 'default',
      returned: 'purple',
    };
    return colors[status] || 'default';
  };

  return (
    <Drawer
      title="Withdrawal Request Details"
      placement="right"
      width={720}
      onClose={onClose}
      open={open}
      zIndex={zIndex}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchWithdrawalDetail}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      {loading && !withdrawal && (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading withdrawal details...</Text>
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message="Error Loading Withdrawal"
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {withdrawal && !loading && (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* Basic Information */}
          <Card title="Request Information" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Request ID">
                <Text code copyable>{withdrawal.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(withdrawal.amount_cents)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusConfig(withdrawal.status).color} icon={getStatusConfig(withdrawal.status).icon}>
                  {withdrawal.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              {withdrawal.status === 'partially_paid' && (
                <>
                  <Descriptions.Item label="Paid Amount">
                    <Text>{formatCurrency(withdrawal.paid_amount_cents || 0)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Remaining Amount">
                    <Text>{formatCurrency(withdrawal.remaining_amount_cents || 0)}</Text>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>

          {/* User Information */}
          <Card title="User Information" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Name">
                {withdrawal.user_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {withdrawal.user_email}
              </Descriptions.Item>
              <Descriptions.Item label="Current Balance">
                {formatCurrency(withdrawal.user_withdrawable_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Balance at Request">
                {formatCurrency(withdrawal.withdrawable_cents_at_request)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Bank Account Snapshot */}
          {withdrawal.bank_account_snapshot && (
            <Card title="Destination Bank Account" size="small">
              <Descriptions column={1} bordered size="small">
                {withdrawal.bank_account_snapshot.institution_name && (
                  <Descriptions.Item label="Institution">
                    {withdrawal.bank_account_snapshot.institution_name}
                  </Descriptions.Item>
                )}
                {withdrawal.bank_account_snapshot.account_name && (
                  <Descriptions.Item label="Account">
                    {withdrawal.bank_account_snapshot.account_name}
                  </Descriptions.Item>
                )}
                {withdrawal.bank_account_snapshot.account_type && (
                  <Descriptions.Item label="Type">
                    {withdrawal.bank_account_snapshot.account_type}
                  </Descriptions.Item>
                )}
                {withdrawal.bank_account_snapshot.mask && (
                  <Descriptions.Item label="Last 4">
                    ****{withdrawal.bank_account_snapshot.mask}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Linked Transfers */}
          {withdrawal.transfers && withdrawal.transfers.length > 0 && (
            <Card
              title={
                <Space>
                  <DollarOutlined />
                  <span>Linked Payout Transfers ({withdrawal.transfers.length})</span>
                </Space>
              }
              size="small"
            >
              <List
                dataSource={withdrawal.transfers}
                renderItem={(transfer) => (
                  <List.Item
                    actions={[
                      onTransferClick && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => onTransferClick(transfer.id)}
                        >
                          View Details
                        </Button>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{formatCurrency(transfer.amount_cents)}</Text>
                          <Tag color={getTransferStatusColor(transfer.status)}>
                            {transfer.status.toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>
                            Transfer ID: <Text code>{transfer.id}</Text>
                          </Text></div>
                          {transfer.provider_transfer_id && (
                            <div><Text type="secondary" style={{ fontSize: 11 }} code>
                              {transfer.provider_transfer_id}
                            </Text></div>
                          )}
                          <div><Text type="secondary" style={{ fontSize: 12 }}>
                            Created: {formatDateTime(transfer.created_at)}
                          </Text></div>
                          {transfer.completed_at && (
                            <div><Text type="secondary" style={{ fontSize: 12 }}>
                              Completed: {formatDateTime(transfer.completed_at)}
                            </Text></div>
                          )}
                          {transfer.failure_reason && (
                            <div><Text type="danger" style={{ fontSize: 12 }}>
                              {transfer.failure_reason}
                            </Text></div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* Timeline */}
          <Card title="Timeline" size="small">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Requested">
                {formatDateTime(withdrawal.requested_at)}
              </Descriptions.Item>
              {withdrawal.resolved_at && (
                <Descriptions.Item label="Resolved">
                  {formatDateTime(withdrawal.resolved_at)}
                </Descriptions.Item>
              )}
              {withdrawal.updated_at && (
                <Descriptions.Item label="Last Updated">
                  {formatDateTime(withdrawal.updated_at)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Notes */}
          {withdrawal.user_note && (
            <Card title="User Note" size="small">
              <Text>{withdrawal.user_note}</Text>
            </Card>
          )}

          {withdrawal.admin_note && (
            <Card title="Admin Note" size="small">
              <Text>{withdrawal.admin_note}</Text>
            </Card>
          )}
        </Space>
      )}
    </Drawer>
  );
};

export default WithdrawalRequestDetailDrawer;
